import {
  includesEnumValue,
  OrdStatusEnum,
  QuoteStatusEnum,
  toBigWithDefault,
  type DistributionPart,
} from '@talos/kyoko';
import Big from 'big.js';
import { isOrderRow, isQuoteRow, type CareOrderRow, type OrderRow, type QuoteRow } from './types';

const pendingFixingQuoteStatuses = [QuoteStatusEnum.PendingFix];
const fixingAppliedQuoteStatuses = [QuoteStatusEnum.Filled];
const workingQuoteStatuses = [QuoteStatusEnum.Open, QuoteStatusEnum.PendingFill, QuoteStatusEnum.PendingNew];

const pendingFixingOrdStatuses = [OrdStatusEnum.Filled];
const fixingAppliedOrdStatuses = [OrdStatusEnum.DoneForDay];
const workingOrdStatuses = [
  OrdStatusEnum.New,
  OrdStatusEnum.PartiallyFilled,
  OrdStatusEnum.PendingNew,
  OrdStatusEnum.PendingCancel,
  OrdStatusEnum.PendingReplace,
  OrdStatusEnum.Replaced,
];

export interface CareOrderProgressBar {
  id: 'fixingApplied' | 'pendingFixing' | 'working';
  valuePercent: Big;
  valueNotional: Big;
  color: DistributionPart['color'];
  appearance?: DistributionPart['appearance'];
}

export function childRowsToMeterBars(childRows: { data: OrderRow | QuoteRow }[] | null | undefined, totalQty: string) {
  if (childRows == null) {
    throw new Error('missing child rows');
  }
  const fixingApplied: CareOrderProgressBar = {
    id: 'fixingApplied',
    valueNotional: Big(0),
    valuePercent: Big(0),
    color: 'var(--colors-green-lighten)',
    appearance: 'filled',
  } as const;
  const pendingFixing: CareOrderProgressBar = {
    id: 'pendingFixing',
    valueNotional: Big(0),
    valuePercent: Big(0),
    color: 'var(--colors-blue-lighten)',
    appearance: 'filled',
  } as const;
  const working: CareOrderProgressBar = {
    id: 'working',
    valueNotional: Big(0),
    valuePercent: Big(0),
    color: 'var(--colors-blue-lighten)',
    appearance: 'striped',
  } as const;

  // Organize the child rows by status into buckets and then apply them to our progress bar constructs
  const notionals = getCareOrderNotionalByStatus(childRows);
  fixingApplied.valueNotional = notionals.fixingApplied;
  pendingFixing.valueNotional = notionals.pendingFixing;
  working.valueNotional = notionals.working;

  // Compute percentages of total
  for (const target of [fixingApplied, pendingFixing, working]) {
    target.valuePercent = target.valueNotional.div(totalQty);
  }

  return [fixingApplied, pendingFixing, working] as const;
}

/**
 * Given all our child rows, iterate over them and throw the qtys into status buckets fixingApplied, pendingFixing, working
 */
export function getCareOrderNotionalByStatus(childRows: { data: OrderRow | QuoteRow }[]) {
  const fixingApplied = { value: Big(0) };
  const pendingFixing = { value: Big(0) };
  const working = { value: Big(0) };

  // Compute notional values
  for (const row of childRows) {
    let value: string | undefined;
    // Used as a mutable reference
    let target: { value: Big } | undefined = undefined;

    if (isQuoteRow(row.data)) {
      const quote = row.data;
      value = quote.OrderQty;
      if (includesEnumValue(pendingFixingQuoteStatuses, quote.QuoteStatus)) {
        target = pendingFixing;
      } else if (includesEnumValue(fixingAppliedQuoteStatuses, quote.QuoteStatus)) {
        target = fixingApplied;
      } else if (includesEnumValue(workingQuoteStatuses, quote.QuoteStatus)) {
        target = working;
      }
    } else if (isOrderRow(row.data)) {
      const order = row.data;
      value = order.OrderQty;
      if (includesEnumValue(pendingFixingOrdStatuses, order.OrdStatus)) {
        target = pendingFixing;
      } else if (includesEnumValue(fixingAppliedOrdStatuses, order.OrdStatus)) {
        target = fixingApplied;
      } else if (includesEnumValue(workingOrdStatuses, order.OrdStatus)) {
        target = working;
      }
    }

    if (value == null || target == null) {
      continue;
    }

    target.value = target.value.plus(toBigWithDefault(value, 0));
  }

  return { fixingApplied: fixingApplied.value, pendingFixing: pendingFixing.value, working: working.value };
}

export function isCareOrderRowFilledAndPendingFix(
  careOrderRow: CareOrderRow,
  childRows: { data: OrderRow | QuoteRow }[]
) {
  const { pendingFixing } = getCareOrderNotionalByStatus(childRows);
  // The only thing we really want to do here is give more clarity if a CareOrder is Filled, and we're just waiting for PendingFixes.
  if (careOrderRow.OrdStatus === 'Filled' && pendingFixing.gt(0)) {
    return true;
  }

  // Else, return the default status
  return false;
}
